import { Collapse } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledAlert from 'src/components/Feedback/StyledAlert/StyledAlert'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_ADVERSE_ACTION_CASES_BY_APPLICANT,
  UPDATE_ADVERSE_ACTION_CASE,
} from 'src/graphql/adverseActions.graphql'
import { AdverseActionStatus } from 'src/graphql/types/adverseActions'

interface AdverseActionAlertsProps {
  applicantId: string
  adverseActionCases: any[]
}

const AdverseActionAlerts = ({
  applicantId,
  adverseActionCases,
}: AdverseActionAlertsProps) => {
  const [adverseActionsExpanded, { toggle: toggleAdverseActions }] =
    useDisclosure(false)

  const [updateAdverseActionCase, { loading: updateAdverseActionCaseLoading }] =
    useMutation(UPDATE_ADVERSE_ACTION_CASE, {
      refetchQueries: [
        {
          query: GET_ADVERSE_ACTION_CASES_BY_APPLICANT,
          variables: {
            applicantId,
          },
        },
      ],
      onCompleted: () => {
        toast('Adverse action cancelled successfully', 'success')
      },
      onError: () => {
        toast('Failed to cancel adverse action', 'error')
      },
    })

  const handleCancelAdverseAction = async (adverseActionCaseId) => {
    await updateAdverseActionCase({
      variables: {
        adverseActionCaseId,
        input: {
          status: AdverseActionStatus.CANCELLED,
        },
      },
    })
  }

  const hasAdverseActions = adverseActionCases.length > 0

  if (!hasAdverseActions) return null

  const activeAdverseActions = adverseActionCases.filter(
    (action) => action.status !== AdverseActionStatus.CANCELLED
  )

  if (activeAdverseActions.length === 0) return null

  const pendingActions = activeAdverseActions.filter(
    (action) => action.status !== AdverseActionStatus.COMPLETED
  )
  const firstPendingAction = pendingActions[0]
  const additionalActions = activeAdverseActions.slice(1)

  const renderAdverseActionAlert = (adverseAction) => (
    <StyledAlert
      key={adverseAction.adverseActionCaseId}
      variant={
        adverseAction.status === AdverseActionStatus.COMPLETED
          ? 'brand'
          : 'error'
      }
      title={
        adverseAction.status === AdverseActionStatus.COMPLETED
          ? 'Adverse Action Completed'
          : 'Adverse Action in Progress'
      }
      description={
        adverseAction.status === AdverseActionStatus.COMPLETED
          ? 'This applicant was sent their final notice on ' +
            dayjs(adverseAction.finalNoticeScheduledFor).format(
              'MM/DD/YYYY [at] hh:mm a'
            ) +
            '.'
          : 'This applicant will be sent a final notice on ' +
            dayjs(adverseAction.finalNoticeScheduledFor).format(
              'MM/DD/YYYY [at] hh:mm a'
            ) +
            '.'
      }
      actionButton={
        adverseAction.status !== AdverseActionStatus.COMPLETED && (
          <DNButton
            loading={updateAdverseActionCaseLoading}
            className="w-20"
            variant="error"
            onClick={() =>
              handleCancelAdverseAction(adverseAction.adverseActionCaseId)
            }
          >
            Cancel
          </DNButton>
        )
      }
    />
  )

  return (
    <div className="flex flex-col gap-3">
      {/* Show first pending action or first action if no pending */}
      <div
        onClick={
          activeAdverseActions.length > 1 ? toggleAdverseActions : undefined
        }
        onKeyDown={(e) => {
          if (
            activeAdverseActions.length > 1 &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault()
            toggleAdverseActions()
          }
        }}
        tabIndex={activeAdverseActions.length > 1 ? 0 : undefined}
        role={activeAdverseActions.length > 1 ? 'button' : undefined}
        aria-expanded={
          activeAdverseActions.length > 1 ? adverseActionsExpanded : undefined
        }
        className={activeAdverseActions.length > 1 ? 'cursor-pointer' : ''}
      >
        {renderAdverseActionAlert(
          firstPendingAction || activeAdverseActions[0]
        )}
        {activeAdverseActions.length > 1 && (
          <div className="mt-2 text-center text-sm text-doubleNickel-gray-600">
            {adverseActionsExpanded ? 'Hide' : 'Show'}{' '}
            {additionalActions.length} more adverse action
            {additionalActions.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Collapsible additional actions */}
      <Collapse in={adverseActionsExpanded}>
        <div className="flex flex-col gap-3">
          {additionalActions.map((adverseAction) =>
            renderAdverseActionAlert(adverseAction)
          )}
        </div>
      </Collapse>
    </div>
  )
}

export default AdverseActionAlerts
